/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";
import RTL from "layouts/rtl";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import AgentSignIn from "layouts/authentication/agent-sign-in";
import SignUp from "layouts/authentication/sign-up";
import PermissionGroups from "layouts/configuration/permission-groups";
import Admins from "layouts/configuration/admins";
import RedirectIfAuthenticated from "components/Common/RedirectIfAuthenticated";
import LoyaltyAgents from "layouts/loyalty/agents";
import LoyaltyCustomers from "layouts/loyalty/customers";
import LoyaltyTransactions from "layouts/loyalty/transactions";
import AgentTopup from "layouts/loyalty/agent-topup";
import AgentRedeem from "layouts/loyalty/agent-redeem";
import ResetPassword from "layouts/authentication/reset-password";
import CustomerResetPin from "layouts/authentication/customer-auth/customer-reset-pin";

// @mui icons
import Icon from "@mui/material/Icon";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import DvrIcon from "@mui/icons-material/Dvr";
import GroupsIcon from "@mui/icons-material/Groups";

const routes = [
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <AddIcon fontSize="small">login</AddIcon>,
    route: "/authentication/sign-in",
    component: (
      <RedirectIfAuthenticated>
        <SignIn />
      </RedirectIfAuthenticated>
    ),
  },
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Agent Sign In",
    key: "agent-sign-in",
    icon: <AddIcon fontSize="small">login</AddIcon>,
    route: "/authentication/agent-sign-in",
    component: (
      <RedirectIfAuthenticated>
        <AgentSignIn />
      </RedirectIfAuthenticated>
    ),
  },
  {
    addToMainMenu: true,
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <DashboardIcon fontSize="small" />,
    route: "/dashboard",
    protected: true,
    component: <Dashboard />,
    roles: ["ADMIN", "AGENT"],
  },
  {
    addToMainMenu: true,
    type: "collapse",
    name: "Transactions",
    key: "transactions",
    icon: <DvrIcon fontSize="small" />,
    route: "/transactions",
    component: <LoyaltyTransactions />,
    roles: ["ADMIN", "AGENT"],
    protected: true,
  },
  {
    addToMainMenu: true,
    type: "collapse",
    name: "Agents",
    key: "agents",
    icon: <GroupsIcon fontSize="small" />,
    route: "/agents",
    component: <LoyaltyAgents />,
    roles: ["ADMIN"],
    protected: true,
  },
  {
    addToMainMenu: true,
    type: "collapse",
    name: "Customers",
    key: "customers",
    icon: <GroupsIcon fontSize="small" />,
    route: "/customers",
    component: <LoyaltyCustomers />,
    roles: ["ADMIN", "AGENT"],
    protected: true,
  },
  {
    addToMainMenu: true,
    type: "collapse",
    name: "Configuration",
    key: "settings",
    protected: true,
    icon: <MenuIcon fontSize="small" />,
    collapse: [
      {
        addToMainMenu: true,
        type: "collapse",
        collapse: [],
        name: "Permission Groups",
        key: "permission-groups",
        icon: <GroupWorkIcon fontSize="small" />,
        route: "/configuration/permission-groups",
        permission: "VIEW PERMISSION GROUP",
        protected: true,
        component: <PermissionGroups />,
      },
      {
        addToMainMenu: true,
        type: "collapse",
        collapse: [],
        name: "Admin Users",
        key: "admin-users",
        icon: <PeopleIcon fontSize="small" />,
        route: "/configuration/admin",
        permission: "VIEW ADMINS",
        protected: true,
        component: <Admins />,
      },
    ],
  },
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Profile",
    key: "profile",
    protected: true,
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
  },
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Reset Password",
    key: "sign-up",
    icon: <Icon fontSize="small">password</Icon>,
    route: "/authentication/reset-password",
    component: <ResetPassword />,
  },
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Redeem",
    key: "redeem",
    icon: <Icon fontSize="small">password</Icon>,
    route: "/loyalty/redeem",
    component: <AgentRedeem />,
  },
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Top Up",
    key: "topup",
    icon: <Icon fontSize="small">password</Icon>,
    route: "/loyalty/topup",
    component: <AgentTopup />,
  },
  {
    type: "route",
    name: "Reset PIN",
    key: "customer-reset-pin",
    route: "/authentication/customer-reset-pin",
    component: <CustomerResetPin />,
    protected: false,
  },
];

export default routes;
